import { ShuffleIcon } from '@phosphor-icons/react';
import { Num, Plural, T, Var, useGT } from 'gt-react';
import {
  WORKBOOK_SAMPLE_METHODS,
  type WorkbookResult,
  type WorkbookSampleMethod,
  type WorkbookSheet,
  type WorkbookValidation,
} from '@erp/shared-types';
import { isDateColumnType, resultColumns } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useValidationText } from './useValidationText';
import type { useWorksheetSample } from './useWorksheetSample';
import type { WorksheetResultView } from './useWorksheetResultView';
import { reshuffled, sampleOf } from './worksheetSamples';
import { failing, validationStates } from './worksheetValidations';

type SampleView = ReturnType<typeof useWorksheetSample>;

/**
 * Says a Large Result's grid shows a Sample, how it was taken and how many
 * rows, and switches how it is taken. It sits beside the grid's row count.
 */
export function WorksheetSampleControl({
  sheet,
  result,
  view,
}: {
  sheet: WorkbookSheet;
  result: WorkbookResult;
  view: SampleView;
}) {
  const gt = useGT();
  if (!view.sampled) return null;
  const columns = {
    any: result.columns,
    dates: resultColumns(sheet, result.columns)
      .filter((column) => isDateColumnType(column.type))
      .map((column) => column.name),
  };
  const methods: Record<WorkbookSampleMethod, string> = {
    first: gt('First rows', { context: 'worksheet sample method' }),
    last: gt('Last rows', { context: 'worksheet sample method' }),
    random: gt('Random rows', { context: 'worksheet sample method' }),
    stratified: gt('Stratified by a column', {
      context: 'worksheet sample method',
    }),
    recent: gt('Most recent by a date', { context: 'worksheet sample method' }),
  };
  const shown = view.focus?.rows.length ?? result.rows.length;
  const { chosen } = view;
  return (
    <div
      role="group"
      aria-label={gt('Sample', { context: 'worksheet sample controls' })}
      className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
    >
      <span role="status" className="tabular-nums">
        <T context="worksheet sample label: how many of the Result's rows the grid shows, beside the Result's row count">
          Showing a sample of <Num>{shown}</Num>. Validations and outputs use
          every row.
        </T>
      </span>
      <Select
        disabled={!view.switchable}
        value={chosen.method}
        onValueChange={(value) => {
          const method = WORKBOOK_SAMPLE_METHODS.find((item) => item === value);
          if (method) view.choose(sampleOf(method, chosen, columns));
        }}
      >
        <SelectTrigger
          className="h-7 w-auto gap-1 text-xs"
          aria-label={gt('How the sample is taken', {
            context: 'worksheet sample method',
          })}
          title={
            view.switchable
              ? undefined
              : gt('Run the workbook to take another sample.', {
                  context: 'worksheet sample method unavailable',
                })
          }
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {WORKBOOK_SAMPLE_METHODS.filter(
            (method) => method !== 'recent' || columns.dates.length > 0,
          ).map((method) => (
            <SelectItem key={method} value={method}>
              {methods[method]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {'column' in chosen && (
        <Select
          disabled={!view.switchable}
          value={chosen.column}
          onValueChange={(column) => view.choose({ ...chosen, column })}
        >
          <SelectTrigger
            className="h-7 w-auto gap-1 text-xs"
            aria-label={gt('Sample column', {
              context: 'worksheet sample column',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(chosen.method === 'recent' ? columns.dates : columns.any).map(
              (column) => (
                <SelectItem key={column} value={column}>
                  {column}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
      )}
      {'seed' in chosen && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          disabled={!view.switchable}
          onClick={() => view.choose(reshuffled(chosen))}
        >
          <ShuffleIcon />
          <T context="worksheet sample reshuffle action">New sample</T>
        </Button>
      )}
      {view.pending && (
        <span role="status">
          <T context="worksheet sample loading">Taking the sample…</T>
        </span>
      )}
      {view.error && (
        <span role="alert" className="text-destructive">
          {view.error}
        </span>
      )}
    </div>
  );
}

/**
 * The Worksheet's Validations over the shown Result: how many passed, each
 * failing one with its failing row count — pressed, the grid shows its
 * failing rows — and how many are not checked yet.
 */
export function WorksheetValidationSummary({
  sheet,
  result,
  focused,
  focus,
}: {
  sheet: WorkbookSheet;
  result: WorkbookResult | undefined;
  /** The Validation whose failing rows the grid shows, if any. */
  focused: string | null;
  focus: (id: string | null) => void;
}) {
  const gt = useGT();
  const text = useValidationText();
  const rules = sheet.validations ?? [];
  if (!rules.length) return null;
  const states = validationStates(sheet, result);
  const failed = rules.flatMap((rule) => {
    const state = states.get(rule.id);
    return state?.kind === 'checked' && failing(state)
      ? [{ rule, check: state.check }]
      : [];
  });
  const passed = rules.filter((rule) => {
    const state = states.get(rule.id);
    return state?.kind === 'checked' && !failing(state);
  }).length;
  const unchecked = rules.length - failed.length - passed;
  return (
    <div
      role="group"
      aria-label={gt('Validations', {
        context: 'worksheet validation summary',
      })}
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground tabular-nums"
    >
      <span className="font-medium text-foreground">
        <T context="worksheet validation summary heading">Validations</T>
      </span>
      {passed > 0 && (
        <span className="text-green-700 dark:text-green-400">
          <T context="worksheet validations passed count">
            <Num>{passed}</Num> passed
          </T>
        </span>
      )}
      {failed.map(({ rule, check }) => {
        const pressed = focused === rule.id;
        return (
          <Button
            key={rule.id}
            type="button"
            size="sm"
            variant="ghost"
            aria-pressed={pressed}
            className={cn(
              'h-6 px-1.5 text-xs',
              rule.severity === 'error'
                ? 'text-destructive'
                : 'text-amber-700 dark:text-amber-400',
              pressed && 'bg-muted',
            )}
            title={
              pressed
                ? gt('Show all rows', { context: 'worksheet validation focus' })
                : gt('Show the rows that fail it', {
                    context: 'worksheet validation focus',
                  })
            }
            onClick={() => focus(pressed ? null : rule.id)}
          >
            {text.named(rule)} ·{' '}
            <T context="worksheet validation status: rows failing it">
              <Num>{check.failedRowCount}</Num>{' '}
              <Plural
                n={check.failedRowCount}
                one="row fails"
                other="rows fail"
              />
            </T>
          </Button>
        );
      })}
      {unchecked > 0 && (
        <span>
          <T context="worksheet validations not checked count">
            <Num>{unchecked}</Num> not checked yet
          </T>
        </span>
      )}
    </div>
  );
}

/**
 * A Worksheet Result's Sample, beside the grid's row count, unless the grid
 * shows a Validation's failing rows.
 */
export function WorksheetResultSample({
  sheet,
  result,
  view,
}: {
  sheet: WorkbookSheet;
  result: WorkbookResult | undefined;
  view: WorksheetResultView;
}) {
  if (!result || view.failing) return null;
  return (
    <WorksheetSampleControl sheet={sheet} result={result} view={view.sample} />
  );
}

/**
 * A Worksheet Result's controls above its grid: its Validations and, while the
 * grid shows one's failing rows, which.
 */
export function WorksheetResultControls({
  sheet,
  result,
  view,
}: {
  sheet: WorkbookSheet;
  result: WorkbookResult | undefined;
  view: WorksheetResultView;
}) {
  const rule = sheet.validations?.find(({ id }) => id === view.failingId);
  return (
    <>
      <div className="my-1 flex flex-col gap-1 empty:hidden">
        <WorksheetValidationSummary
          sheet={sheet}
          result={result}
          focused={view.failingId}
          focus={view.showFailing}
        />
      </div>
      {view.failing && rule && (
        <FailingRowsNote
          shown={view.failing.rows.length}
          failed={view.failing.failedRowCount}
          rule={rule}
          showAll={() => view.showFailing(null)}
        />
      )}
    </>
  );
}

/** Says the grid shows only the rows failing one Validation, and turns that off. */
function FailingRowsNote({
  shown,
  failed,
  rule,
  showAll,
}: {
  shown: number;
  failed: number;
  rule: WorkbookValidation;
  showAll: () => void;
}) {
  const text = useValidationText();
  return (
    <p
      role="status"
      className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground tabular-nums"
    >
      <T context="worksheet grid shows the rows failing a validation">
        Showing <Num>{shown}</Num> of <Num>{failed}</Num> rows that fail{' '}
        <Var>{text.named(rule)}</Var>.
      </T>
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        onClick={showAll}
      >
        <T context="worksheet validation focus">Show all rows</T>
      </Button>
    </p>
  );
}
